import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CommentWithUser, UpdateCommentRequest, ReactionType, Reaction } from '../comments.models';
import { AttachmentFile, CommentsAttachmentsService } from '../comments-attachments.service';
import { CommentsService } from '../../services/comments.service';
import { AppService } from '../../../app.service';
import { ChatService } from '../../services/chat.service';

/** Listed file plus presigned URL for thumbnails / preview */
interface AttachmentRow {
  file: AttachmentFile;
  url: string;
}

/** Pending file in the edit composer with a blob URL for thumbnails / preview */
interface ComposerAttachment {
  file: File;
  previewUrl: string;
}

@Component({
  selector: 'app-comment-item',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    EditorModule,
    TooltipModule,
    ConfirmDialogModule,
    OverlayPanelModule
  ],
  providers: [ConfirmationService],
  templateUrl: './comment-item.component.html',
  styleUrl: './comment-item.component.scss'
})
export class CommentItemComponent implements OnChanges, OnInit, OnDestroy {
  @Input() comment!: CommentWithUser;
  /** Parent comment when this item is a reply (for "Reply to …" context). */
  @Input() parentComment: CommentWithUser | null = null;
  @Input() currentUserId?: number;
  @Input() isAdmin: boolean = false;
  @Input() attachmentTags: string[] = [];
  
  @Output() replyClicked = new EventEmitter<CommentWithUser>();
  @Output() commentUpdated = new EventEmitter<void>();
  @Output() commentDeleted = new EventEmitter<number>();

  isEditing = false;
  /** Rich HTML from p-editor (nullable like PrimeNG Editor). */
  editText: string | null = '';
  editorModules: Record<string, unknown> = {};
  isSaving = false;
  attachmentRows: AttachmentRow[] = [];
  loadingAttachments = false;
  editAttachments: ComposerAttachment[] = [];

  previewDialogVisible = false;
  previewRow: AttachmentRow | null = null;
  pendingPreviewAttachment: ComposerAttachment | null = null;

  // Available reaction types with icons and labels
  // availableReactions: { type: ReactionType; icon: string; label: string }[] = [
  //   { type: 'like', icon: 'pi-thumbs-up', label: 'Like' },
  //   { type: 'helpful', icon: 'pi-check-circle', label: 'Helpful' },
  //   { type: 'resolved', icon: 'pi-check', label: 'Resolved' },
  //   { type: 'important', icon: 'pi-exclamation-circle', label: 'Important' },
  //   { type: 'question', icon: 'pi-question-circle', label: 'Question' }
  // ];
  availableReactions = [
    { type: 'like', emoji: '👍', label: 'Like' },
    { type: 'clap', emoji: '👏', label: 'Clap' },
    { type: 'fire', emoji: '🔥', label: 'Fire' },
    { type: 'love', emoji: '❤️', label: 'Love' },
    { type: 'wow', emoji: '😮', label: 'Wow' },
    { type: 'thinking', emoji: '🤔', label: 'Thinking' }
  ];

  constructor(
    public commentsService: CommentsService,
    private commentsAttachmentsService: CommentsAttachmentsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public appService: AppService,
    private sanitizer: DomSanitizer,
    private chatService: ChatService
  ) {}

  ngOnInit(): void {
    this.editorModules = {
      mention: {
        positioningStrategy: 'fixed',
        allowedChars: /^[A-Za-z0-9\s]*$/,
        mentionDenotationChars: ['@'],
        source: (searchTerm: string, renderList: (values: { id: number; value: string }[], term: string) => void) => {
          this.commentsService.searchUsers(searchTerm).subscribe((users) => {
            const values = users.map((user) => ({
              id: user.id,
              value: `${user.first_name} ${user.last_name}`,
            }));
            renderList(values, searchTerm);
          });
        },
      },
    };
  }

  ngOnDestroy(): void {
    this.clearEditAttachments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['comment']?.currentValue?.id && !this.isDeletedPlaceholder()) {
      this.loadAttachments();
    } else if (this.isDeletedPlaceholder()) {
      this.attachmentRows = [];
      this.loadingAttachments = false;
    }
  }

  /** Soft-deleted comment kept in the thread only because replies remain. */
  isDeletedPlaceholder(): boolean {
    return !!this.comment.is_deleted;
  }

  /**
   * Check if current user can edit this comment
   */
  canEdit(): boolean {
    if (this.isDeletedPlaceholder()) return false;
    if (!this.currentUserId) return false;
    return this.comment.created_by === this.currentUserId || this.isAdmin;
  }

  /**
   * Check if current user can delete this comment
   */
  canDelete(): boolean {
    if (this.isDeletedPlaceholder()) return false;
    if (!this.currentUserId) return false;
    return this.comment.created_by === this.currentUserId || this.isAdmin;
  }

  isReply(): boolean {
    return this.parentComment != null || (this.comment.parent_comment_id != null && this.comment.thread_depth > 0);
  }

  getReplyToName(): string {
    if (!this.parentComment) {
      return '';
    }
    return this.commentsService.getUserDisplayName(this.parentComment.created_by_user);
  }

  /** Short plain-text preview of the parent comment (for reply context). */
  getParentCommentPreview(): string {
    if (this.parentComment?.is_deleted) {
      return 'Comment Deleted';
    }
    if (!this.parentComment?.comment_text) {
      return '';
    }
    try {
      const doc = new DOMParser().parseFromString(this.parentComment.comment_text, 'text/html');
      const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) {
        return '';
      }
      return text.length > 100 ? `${text.slice(0, 100)}…` : text;
    } catch {
      const plain = this.parentComment.comment_text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      return plain.length > 100 ? `${plain.slice(0, 100)}…` : plain;
    }
  }

  /**
   * Get display name for comment creator
   */
  getCreatorName(): string {
    return this.commentsService.getUserDisplayName(this.comment.created_by_user);
  }

  /**
   * Get relative time string
   */
  getTimeAgo(): string {
    return this.commentsService.getRelativeTime(this.comment.created_at);
  }

  /**
   * Get profile picture URL or initials
   */
  getProfilePicUrl(): string | null {
    return this.comment.created_by_user?.profile_pic_url || null;
  }

  /**
   * Get user initials for avatar fallback
   */
  getUserInitials(): string {
    const user = this.comment.created_by_user;
    if (!user) return 'U';
    
    const first = user.first_name?.charAt(0) || '';
    const last = user.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  }

  /**
   * Start editing mode
   */
  startEdit(): void {
    this.editText = this.comment.comment_text ?? '';
    this.clearEditAttachments();
    this.isEditing = true;
  }

  /**
   * Cancel editing
   */
  cancelEdit(): void {
    this.isEditing = false;
    this.editText = '';
    this.clearEditAttachments();
  }

  onEditEditorTextChange(_event: unknown): void {
    // Reserved for parity with comments composer.
  }

  hasEditEditorText(): boolean {
    return !!this.trimmedEditorHtml(this.editText);
  }

  private trimmedEditorHtml(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  onEditFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const added = Array.from(input.files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    this.editAttachments = [...this.editAttachments, ...added];
    input.value = '';
  }

  removeEditAttachment(index: number): void {
    const removed = this.editAttachments[index];
    if (removed) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.editAttachments = this.editAttachments.filter((_, i) => i !== index);
  }

  openPendingAttachmentPreview(att: ComposerAttachment): void {
    this.previewRow = null;
    this.pendingPreviewAttachment = att;
    this.previewDialogVisible = true;
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  pendingPreviewPdfSafe(): SafeResourceUrl | null {
    if (!this.pendingPreviewAttachment || !this.isPdfFile(this.pendingPreviewAttachment.file)) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.pendingPreviewAttachment.previewUrl);
  }

  private clearEditAttachments(): void {
    this.editAttachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    this.editAttachments = [];
    this.pendingPreviewAttachment = null;
  }

  private async uploadEditAttachmentsIfNeeded(files: File[]): Promise<void> {
    if (!files.length) return;

    const summary = await firstValueFrom(
      this.commentsAttachmentsService.uploadCommentAttachments(this.comment.id, files, this.attachmentTags)
    );

    if (summary.failureCount > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Partial Upload',
        detail: `${summary.successCount} uploaded, ${summary.failureCount} failed`,
      });
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Attachments Uploaded',
      detail: `${summary.successCount} attachment${summary.successCount === 1 ? '' : 's'} uploaded`,
    });
  }

  /**
   * Save edited comment
   */
  saveEdit(): void {
    const body = this.trimmedEditorHtml(this.editText);
    if (!body) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Comment text cannot be empty'
      });
      return;
    }

    this.isSaving = true;
    const request: UpdateCommentRequest = {
      comment_text: body
    };
    const pendingFiles = this.editAttachments.map((a) => a.file);

    this.commentsService.updateComment(this.comment.id, request).subscribe({
      next: async () => {
        try {
          await this.uploadEditAttachmentsIfNeeded(pendingFiles);
        } catch {
          // Keep comment update successful even if upload fails unexpectedly.
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Comment updated successfully'
        });
        this.isEditing = false;
        this.isSaving = false;
        this.clearEditAttachments();
        this.commentUpdated.emit();
      },
      error: (error) => {
        console.error('Failed to update comment:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update comment'
        });
        this.isSaving = false;
      }
    });
  }

  /**
   * Delete comment with confirmation
   */
  deleteComment(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this comment?',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.commentsService.deleteComment(this.comment.id).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Comment deleted successfully'
            });
            this.commentDeleted.emit(this.comment.id);
          },
          error: (error) => {
            console.error('Failed to delete comment:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete comment'
            });
          }
        });
      }
    });
  }

  /**
   * Reply to this comment
   */
  reply(): void {
    this.replyClicked.emit(this.comment);
  }

  /**
   * Toggle pin status (admin only)
   */
  togglePin(): void {
    if (!this.isAdmin) return;

    const request: UpdateCommentRequest = {
      is_pinned: !this.comment.is_pinned
    };

    this.commentsService.updateComment(this.comment.id, request).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.comment.is_pinned ? 'Comment unpinned' : 'Comment pinned'
        });
        this.commentUpdated.emit();
      },
      error: (error) => {
        console.error('Failed to toggle pin:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update comment'
        });
      }
    });
  }

  /**
   * Get grouped reactions with counts
   */
  getReactionSummary(): { type: ReactionType; emoji: string; label: string; count: number; userReacted: boolean }[] {
    if (!this.comment.reactions || this.comment.reactions.length === 0) {
      return [];
    }

    const reactionMap = new Map<ReactionType, { count: number; userReacted: boolean }>();
    
    this.comment.reactions.forEach(reaction => {
      const existing = reactionMap.get(reaction.type) || { count: 0, userReacted: false };
      existing.count++;
      if (reaction.user_id === this.currentUserId) {
        existing.userReacted = true;
      }
      reactionMap.set(reaction.type, existing);
    });

    return Array.from(reactionMap.entries()).map(([type, data]) => {
      const reactionInfo = this.availableReactions.find(r => r.type === type);
      return {
        type,
        emoji: reactionInfo?.emoji || '👍', 
        // icon: reactionInfo?.icon || 'pi-circle',
        label: reactionInfo?.label || type,
        count: data.count,
        userReacted: data.userReacted
      };
    });
  }

  /**
   * Toggle a reaction on this comment
   */
  toggleReaction(reactionType: ReactionType, overlayPanel?: any): void {
    if (!this.currentUserId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Logged In',
        detail: 'You must be logged in to react'
      });
      return;
    }

    const userReaction = this.comment.reactions?.find(
      r => r.type === reactionType && r.user_id === this.currentUserId
    );

    const action: 'add' | 'remove' = userReaction ? 'remove' : 'add';

    this.chatService
      .sendReaction(this.comment.id, reactionType, action)
      .subscribe({
        next: () => {
          if (overlayPanel) {
            overlayPanel.hide();
          }
        },
        error: (error) => {
          console.error(`Failed to ${action} reaction:`, error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to ${action} reaction`
          });
        }
      });
  }

  /**
   * Process comment text to highlight mentions
   */
  getFormattedCommentText(): string {
    let text = this.comment.comment_text;
    
    // Simple @ mention highlighting - could be enhanced with user lookup
    text = text.replace(/@(\w+)/g, '<span class="mention">@$1</span>');
    
    return text;
  }

  private loadAttachments(): void {
    if (!this.comment?.id) {
      this.attachmentRows = [];
      return;
    }
    this.loadingAttachments = true;
    this.commentsAttachmentsService
      .listCommentAttachments(this.comment.id)
      .pipe(
        switchMap((files) => {
          if (!files.length) {
            return of<AttachmentRow[]>([]);
          }
          return forkJoin(
            files.map((f) =>
              this.commentsAttachmentsService.getDownloadUrl(f.object_key).pipe(
                map((url) => ({ file: f, url: url || '' })),
                catchError(() => of({ file: f, url: '' }))
              )
            )
          );
        }),
        catchError(() => of([]))
      )
      .subscribe({
        next: (rows) => {
          this.attachmentRows = rows;
          this.loadingAttachments = false;
        },
        error: () => {
          this.attachmentRows = [];
          this.loadingAttachments = false;
        },
      });
  }

  isImageAttachment(row: AttachmentRow): boolean {
    const m = row.file.mime_type;
    if (m) {
      return m.startsWith('image/');
    }
    return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(row.file.name);
  }

  isPdfAttachment(row: AttachmentRow): boolean {
    const m = row.file.mime_type;
    if (m) {
      return m === 'application/pdf';
    }
    return /\.pdf$/i.test(row.file.name);
  }

  getFileExtension(fileName: string): string {
    const i = fileName.lastIndexOf('.');
    if (i < 0 || i === fileName.length - 1) {
      return 'FILE';
    }
    return fileName.slice(i + 1, i + 5).toUpperCase();
  }

  openAttachmentPreview(row: AttachmentRow): void {
    if (!row.url) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Preview unavailable',
        detail: 'Could not load a download link for this file.',
      });
      return;
    }
    this.pendingPreviewAttachment = null;
    this.previewRow = row;
    this.previewDialogVisible = true;
  }

  onPreviewDialogHide(): void {
    this.previewRow = null;
    this.pendingPreviewAttachment = null;
  }

  previewPdfSafe(): SafeResourceUrl | null {
    if (!this.previewRow?.url || !this.isPdfAttachment(this.previewRow)) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.previewRow.url);
  }

  openAttachmentInNewTab(row: AttachmentRow): void {
    if (!row.url) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to open attachment',
      });
      return;
    }
    window.open(row.url, '_blank', 'noopener,noreferrer');
  }
}
